import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { invitationService } from "../../../services/invitationService";
import { GoPerson } from "react-icons/go";
import MultiEmailSelect from "../../../components/ChangeSelect";
import {
  communityService,
  groupService,
  ServiceOnMount,
} from "../../../services";
import { toast } from "react-toastify";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";

const NewUserPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("general");
  const [isLoading, setIsLoading] = useState(false);
  const [userInfo, setUserInfo] = useState({
    name: "",
    role: "",
    email: "",
    communityId: "", // Add communityId to track selected community
    groupId: "", // Add groupId to track selected group
    membershipRole: "", // Add membershipRole
  });

  const [errors, setErrors] = useState({
    general: [],
  });

  const handleTabSwitch = (tab) => {
    setActiveTab(tab);
  };

  const handleEmailChange = (email) => {
    setUserInfo((prev) => ({
      ...prev,
      email: Array.isArray(email) ? email[0] : email, // ensures it's always a string
    }));
  };

  // Validation function for all fields
  const validateForm = () => {
    let userErrors = [];

    if (!userInfo.name) userErrors.push("Name");
    if (!userInfo.role) userErrors.push("Role");
    if (!userInfo.email) userErrors.push("Email");

    setErrors({ general: userErrors });

    return userErrors.length === 0;
  };

  const handleReassign = async () => {
    if (!validateForm()) {
      alert("Please fill in all required fields.");
      return;
    }

    // Prepare the base invite data
    let inviteData = {
      Email: userInfo.email,
      Name: userInfo.name,
    };

    // Handle different role cases
    if (userInfo.role === "U") {
      // For Members, use the membership role and include community/group info
      if (!userInfo.communityId) {
        alert("Please select a community for the member");
        return;
      }

      inviteData = {
        ...inviteData,
        Role: userInfo.membershipRole, // Use membership role instead of account role
        CommunityId: userInfo.communityId,
        GroupId: userInfo.groupId || null, // Can be null if no group selected
      };
    } else {
      // For Super Admin, POC Attendant, and Collector
      inviteData = {
        ...inviteData,
        Role: userInfo.role, // Use the account role directly
        CommunityId: null, // These roles typically don't belong to specific communities
        GroupId: null, // or groups
      };
    }

    try {
      setIsLoading(true);
      console.log("Sending Invite:", inviteData);
      const response = await invitationService.sendInvite(inviteData);
      console.log("Invite Sent Successfully:", response);

      toast.success("🎉 Invitation sent successfully!", {
        autoClose: 2000,
      });

      // Reset form fields after successful invite
      setUserInfo({
        name: "",
        role: "",
        email: "",
        communityId: "",
        groupId: "",
        membershipRole: "",
      });
    } catch (error) {
      console.error("Error Sending Invite:", error);
      toast.error("❌ Error sending invitation. Please Try again later", {
        autoClose: 2000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const communityMap = (item) => ({
    communityId: item._id,
    communityName: item.name,
  });
  const getCommunity = ServiceOnMount(
    communityService.getCommunity,
    communityMap
  );

  console.log("get community", getCommunity);

  const groupMap = (item) => ({
    groupId: item._id,
    groupName: item.name,
    communityId: item.community._id,
    communityName: item.community.name,
    totalMembers: item.total,
    description: item.description,
    dateCreated: item.create_date,
  });

  const getGroups = ServiceOnMount(() => groupService.getGroups(), groupMap);

  console.log("get groups: ", getGroups);

  // Filter groups based on selected community
  const filteredGroups =
    userInfo.communityId && getGroups.data
      ? getGroups.data.filter(
          (group) => group.communityId === userInfo.communityId
        )
      : [];

  // Update handleUserInfoChange to handle all fields
  const handleUserInfoChange = (e) => {
    const { name, value } = e.target;
    setUserInfo((prev) => ({
      ...prev,
      [name]: value,
      // Reset community/group related fields when role changes from Member
      ...(name === "role" &&
        value !== "U" && {
          communityId: "",
          groupId: "",
          membershipRole: "",
        }),
      // Reset groupId when community changes
      ...(name === "communityId" && { groupId: "", membershipRole: "" }),
      // Reset membershipRole when group changes
      ...(name === "groupId" && { membershipRole: "" }),
    }));
  };

  return (
    <div className="flex flex-col md:flex-row min-h-screen">
      <div className="flex-1 flex flex-col">
        {/* Main Content */}
        <div className="p-8 flex flex-col">
          {/* Header Section */}
          <div className="bg-white shadow p-4 sm:p-6 rounded-lg">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              {/* Left Side: Icon + Title */}
              <div className="flex items-center gap-4">
                <div className="bg-teal-100 p-3 sm:p-4 rounded-full">
                  <GoPerson className="w-10 h-10 sm:w-16 sm:h-16 text-teal-700" />
                </div>
                <h1 className="text-base sm:text-lg font-semibold text-[#111827]">
                  Invite a new user
                </h1>
              </div>

              {/* Right Side: Buttons */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 w-full sm:w-auto">
                <Button
                  variant={"hollowGreen"}
                  onClick={() => navigate("/users")}
                  className="w-full sm:w-auto"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleReassign}
                  disabled={isLoading}
                  className="w-full sm:w-auto"
                >
                  {isLoading ? "Inviting..." : "Invite"}
                </Button>
              </div>
            </div>
          </div>

          {/* Tabs Section */}
          <div className="flex">
            {/* General Info Tab */}
            <button
              onClick={() => handleTabSwitch("general")}
              className={`relative mt-5 rounded-tl-xl px-3 pr-10 py-3 text-[14px] font-bold ${
                activeTab === "general"
                  ? "text-[#111827] bg-white"
                  : "text-[#14B8A6] bg-[#F0FDFA]"
              }`}
              style={{
                clipPath: "polygon(0 0, 70% 0, 100% 100%, 0% 100%)",
              }}
            >
              General Info
            </button>
          </div>

          {/* Form Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 p-6 mt-0 bg-white shadow rounded-r-lg rounded-b-lg border-gray-300">
            {activeTab === "general" && (
              <>
                {/* Name */}
                <div>
                  <Label>Name</Label>
                  <Input
                    type="text"
                    name="name"
                    value={userInfo.name}
                    onChange={handleUserInfoChange}
                    placeholder="Enter Users Name"
                  />
                  {errors.general.includes("Name") && (
                    <p className="text-red-500 text-xs">Name is required</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <Label>Email</Label>
                  <MultiEmailSelect
                    selectedEmails={[userInfo.email]}
                    onChange={handleEmailChange}
                    isDisabled={false}
                    isMulti={false}
                    className="text-left"
                  />
                  {errors.general.includes("Email") && (
                    <p className="text-red-500 text-xs">Email is required</p>
                  )}
                </div>

                {/* Role Dropdown */}
                <div>
                  <Label>Designation</Label>
                  <Select
                    value={userInfo.role}
                    onValueChange={(value) =>
                      handleUserInfoChange({
                        target: {
                          name: "role",
                          value: value,
                        },
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a Role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="A">Super Admin</SelectItem>
                      <SelectItem value="POCA">POC Attendant</SelectItem>
                      <SelectItem value="C">Collector</SelectItem>
                      <SelectItem value="U">Public</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.general.includes("Role") && (
                    <p className="text-red-500 text-xs">Role is required</p>
                  )}
                </div>

                {/* Only show community/group section if Member is selected */}
                {userInfo.role === "U" && (
                  <>
                    <hr className="col-span-2" />

                    {/* Community Dropdown */}
                    <div>
                      <Label>Community Name</Label>
                      <Select
                        name="communityId"
                        value={userInfo.communityId || undefined}
                        onValueChange={(value) =>
                          handleUserInfoChange({
                            target: {
                              name: "communityId",
                              value: value,
                            },
                          })
                        }
                        disabled={getCommunity.loading}
                        required
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={
                              getCommunity.loading
                                ? "Loading communities..."
                                : "Select a Community"
                            }
                          />
                        </SelectTrigger>
                        {!getCommunity.loading && (
                          <SelectContent>
                            {getCommunity.data?.map((community) => (
                              <SelectItem
                                key={community.communityId}
                                value={community.communityId}
                              >
                                {community.communityName}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        )}
                      </Select>
                    </div>

                    {/* Group Dropdown - only shown if community is selected */}
                    {userInfo.communityId && filteredGroups.length > 0 && (
                      <div>
                        <Label>Group Name (Required)</Label>
                        <Select
                          name="groupId"
                          value={userInfo.groupId || undefined}
                          onValueChange={(value) =>
                            handleUserInfoChange({
                              target: {
                                name: "groupId",
                                value: value,
                              },
                            })
                          }
                          disabled={getGroups.loading}
                          required
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select a Group" />
                          </SelectTrigger>
                          <SelectContent>
                            {getGroups.loading ? (
                              <div className="py-1 px-2 text-sm text-gray-500">
                                Loading groups...
                              </div>
                            ) : (
                              filteredGroups.map((group) => (
                                <SelectItem
                                  key={group.groupId}
                                  value={group.groupId}
                                >
                                  {group.groupName}
                                </SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {/* Membership Role Dropdown */}
                    <div>
                      <Label>Membership Role</Label>
                      <Select
                        name="membershipRole"
                        value={userInfo.membershipRole || undefined}
                        onValueChange={(value) =>
                          handleUserInfoChange({
                            target: {
                              name: "membershipRole",
                              value: value,
                            },
                          })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select a Role" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="CA">Community Admin</SelectItem>
                          {filteredGroups.length > 0 && (
                            <SelectItem value="GL">Group Leader</SelectItem>
                          )}
                          <SelectItem value="U">Member</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewUserPage;
